import * as SubscriptionActionTypes from 'actiontypes/subscription';
import * as SettingsActionTypes from '../actiontypes/settings';

/**
 * Initial state
 * @type {Object}
 */
const initialState = {
  isFetching: false,
  isSaving: false,
  isCreating: false,
  isSuccess: false,
  isDeleting: false,
  isChangingVia: false,
  isResetting: false,
  isUsernameDialogOpen: false,
  isNotificationDialogOpen: false,
  notificationMessage: null,
  isChangingUsername: false,
  isDeactivateDialogVisible: false,
  isEmailLinkDialogVisible: false,
  isUpdatingEmail: false,
  isEmailUpdateDialogVisible: false,
  isUnlinking: false,
  isLinkingEmail: false,
  isDeactivating: false,
  message: null,
  data: null,
  messagePreferences: null,
  subscriptions: null,
};


/**
 * Settings Reducer
 * @param {Object} state
 * @param {[Object]} action
 */
const Settings = (state = initialState, action) => {
  switch (action.type) {
    case SettingsActionTypes.TOGGLE_NOTIFICATION_DIALOG:
      return {
        ...state,
        isNotificationDialogOpen: !state.isNotificationDialogOpen,
      };

    case SettingsActionTypes.FETCH_SETTINGS_REQUESTED:
    case SettingsActionTypes.FETCH_SETTINGS_FAILURE:
      return {
        ...state,
        isFetching: action.isFetching,
      };

    // Listen for changes that get propagated via refreshing the subscription state
    case SubscriptionActionTypes.FETCH_SUCCESS:
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          hasBraintreeSubscription: action.payload.hasSubscription,
        },
      };

    case SettingsActionTypes.FETCH_SETTINGS_SUCCESS:
      return {
        ...state,
        isFetching: action.isFetching,
        data: action.data,
        messagePreferences: {
          messageAboutAnnouncements: action.data.messageAboutAnnouncements,
          messageAboutNastiness: action.data.messageAboutNastiness,
          messageAboutNiceness: action.data.messageAboutNiceness,
          messageAboutStorylets: action.data.storyEventMessage,
        },
        subscriptions: {
          hasBraintreeSubscription: action.data.hasBraintreeSubscription,
          hasAndroidSubscription: action.data.hasAndroidSubscription,
          hasAppleSubscription: action.data.hasAppleSubscription,
        },
      };

    case SettingsActionTypes.UPDATE_MESSAGE_PREFERENCES:
      return {
        ...state,
        messagePreferences: {
          ...state.messagePreferences,
          ...action.messagePreferences,
        },
      };

    case SettingsActionTypes.SAVE_MESSAGE_PREFERENCES_REQUESTED:
    case SettingsActionTypes.SAVE_MESSAGE_PREFERENCES_FAILURE:
      return {
        ...state,
        isSaving: action.isSaving,
      };

    case SettingsActionTypes.SAVE_MESSAGE_PREFERENCES_SUCCESS:
      return {
        ...state,
        isSaving: action.isSaving,
        messagePreferences: {
          ...action.settings,
        },
      };

    case SettingsActionTypes.DELETE_CONTACT_REQUESTED:
    case SettingsActionTypes.DELETE_CONTACT_FAILURE:
      return {
        ...state,
        isDeleting: action.isDeleting,
      };

    case SettingsActionTypes.DELETE_CONTACT_SUCCESS: {
      const updatedContactList = state.contacts.filter(c => (c.id !== action.deleteUserId));

      return {
        ...state,
        isDeleting: action.isDeleting,
        isSuccess: action.isSuccess,
        message: action.message,
        contacts: updatedContactList,
      };
    }

    case SettingsActionTypes.REQUEST_PASSWORD_RESET_REQUESTED:
    case SettingsActionTypes.REQUEST_PASSWORD_RESET_FAILURE:
      return {
        ...state,
        isRequesting: action.isRequesting,
        isSuccess: false,
        message: null,
      };

    case SettingsActionTypes.REQUEST_PASSWORD_RESET_SUCCESS:
      return {
        ...state,
        isRequesting: action.isRequesting,
        isSuccess: action.isSuccess,
        message: action.message,
      };

    case SettingsActionTypes.TOGGLE_USERNAME_DIALOG:
      return {
        ...state,
        isUsernameDialogOpen: !state.isUsernameDialogOpen,
      };

    case SettingsActionTypes.CHANGE_USERNAME_REQUESTED:
    case SettingsActionTypes.CHANGE_USERNAME_FAILURE:
      return {
        ...state,
        isChangingUsername: action.isChangingUsername,
        isSuccess: false,
        message: null,
      };

    case SettingsActionTypes.CHANGE_USERNAME_SUCCESS: {
      const { data } = state;

      return {
        ...state,
        isChangingUsername: action.isChangingUsername,
        isSuccess: action.isSuccess,
        message: action.message,
        data: {
          ...data,
          name: action.isSuccess ? action.username : data.name,
        },
      };
    }

    case SettingsActionTypes.SAVE_MESSAGES_VIA_REQUESTED:
    case SettingsActionTypes.SAVE_MESSAGES_VIA_FAILURE:
      return {
        ...state,
        isChangingVia: action.isChangingVia,
        isSuccess: false,
        message: null,
      };

    case SettingsActionTypes.SAVE_MESSAGES_VIA_SUCCESS:
      return {
        ...state,
        isChangingVia: action.isChangingVia,
        isSuccess: action.isSuccess,
        message: action.message,
      };

    case SettingsActionTypes.TOGGLE_DEACTIVATE_DIALOG:
      return {
        ...state,
        isDeactivateDialogVisible: !state.isDeactivateDialogVisible,
        isDeactivating: false,
      };

    case SettingsActionTypes.DEACTIVATE_ACCOUNT_REQUESTED:
    case SettingsActionTypes.DEACTIVATE_ACCOUNT_FAILURE:
      return {
        ...state,
        isDeactivating: action.isDeactivating,
        isSuccess: false,
        message: null,
      };

    case SettingsActionTypes.DEACTIVATE_ACCOUNT_SUCCESS:
      return {
        ...state,
        isDeactivating: action.isDeactivating,
        isSuccess: action.isSuccess,
        message: action.message,
      };

    case SettingsActionTypes.TOGGLE_EMAIL_LINK_DIALOG:
      return {
        ...state,
        message: null,
        isEmailLinkDialogVisible: !state.isEmailLinkDialogVisible,
      };


    case SettingsActionTypes.LINK_EMAIL_TO_ACCOUNT_REQUESTED:
    case SettingsActionTypes.LINK_EMAIL_TO_ACCOUNT_FAILURE:
      return {
        ...state,
        isLinkingEmail: action.isLinkingEmail,
        isSuccess: false,
        emailMessage: null,
      };

    case SettingsActionTypes.LINK_EMAIL_TO_ACCOUNT_SUCCESS:
      return {
        ...state,
        isLinkingEmail: action.isLinkingEmail,
        isSuccess: action.isSuccess,
        emailMessage: action.message,
      };

    case SettingsActionTypes.TOGGLE_EMAIL_UPDATE_DIALOG:
      return {
        ...state,
        emailMessage: null,
        isEmailUpdateDialogVisible: !state.isEmailUpdateDialogVisible,
      };

    case SettingsActionTypes.UPDATE_EMAIL_REQUESTED:
    case SettingsActionTypes.UPDATE_EMAIL_FAILURE:
      return {
        ...state,
        isUpdatingEmail: action.isUpdatingEmail,
        isSuccess: false,
        emailMessage: null,
      };

    case SettingsActionTypes.UPDATE_EMAIL_SUCCESS:
      return {
        ...state,
        isUpdatingEmail: action.isUpdatingEmail,
        isSuccess: action.isSuccess,
        emailMessage: action.message,
        data: {
          ...state.data,
          emailAddress: action.isSuccess ? action.emailAddress : state.data.emailAddress,
        },
      };

    case SettingsActionTypes.UNLINK_SOCIAL_ACCOUNT_REQUESTED:
    case SettingsActionTypes.UNLINK_SOCIAL_ACCOUNT_FAILURE:
      return {
        ...state,
        isUnlinking: action.isUnlinking,
        isSuccess: false,
      };

    case SettingsActionTypes.UNLINK_SOCIAL_ACCOUNT_SUCCESS:
      return {
        ...state,
        isUnlinking: action.isUnlinking,
        isSuccess: action.isSuccess,
        data: {
          ...state.data,
          twitterAuth: (action.accountType === 'twitter') ? false : state.data.twitterAuth,
          facebookAuth: (action.accountType === 'facebook') ? false : state.data.facebookAuth,
        },
      };

    case SettingsActionTypes.LINK_SOCIAL_ACCOUNT_SUCCESS:
      return {
        ...state,
        isLinking: action.isLinking,
        isSuccess: action.isSuccess,
        isNotificationDialogOpen: !action.isSuccess,
        notificationMessage: action.message,
        data: {
          ...state.data,
          twitterAuth: (action.accountType === 'twitter') ? action.isSuccess : state.data.twitterAuth,
          facebookAuth: (action.accountType === 'facebook') ? action.isSuccess : state.data.facebookAuth,
        },
      };

    case SettingsActionTypes.PASSWORD_RESET_REQUESTED:
    case SettingsActionTypes.PASSWORD_RESET_FAILURE:
      return {
        ...state,
        isResetting: action.isResetting,
        isSuccess: false,
        message: null,
      };

    case SettingsActionTypes.PASSWORD_RESET_SUCCESS:
      return {
        ...state,
        isResetting: action.isResetting,
        isSuccess: action.isSuccess,
        message: action.message,
      };

    case SettingsActionTypes.CLOSE_ALL_MODALS:
      return {
        ...state,
        isDeactivateDialogVisible: false,
        isEmailLinkDialogVisible: false,
        isEmailUpdateDialogVisible: false,
        isUsernameDialogOpen: false,
      };

    default:
      return state;
  }
};

export default Settings;
